import { useRemoteResource } from "./useRemoteResource";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { coalesceInflight } from "../utils/coalesceInflight";
import { writeClassCache } from "../utils/classCache";

// Classes for a show — used to derive the list of rings on offer (score-api
// has no dedicated ring-listing route; `class.ring` is FVD free text, so
// distinct values off the class list is the only source), AND by the Order
// of Go to label each class header (className/classNumber).
//
// #692 — `nonce` is the same entity-refresh beat useRidesForRing/
// useBreaksForRing take: bump it to force a revalidate (a class renamed/
// renumbered, or newly added, mid-show).
//
// #2190 — no local cache: a network failure leaves whatever classes are
// already on screen exactly as they are (see useRemoteResource). A null
// showId is a no-op (nothing picked yet).
//
// #2703 — `keyed` (opt-in) gives the resource a [showId] key, the way
// useRidesForRing keys on [showId, ring]: `loading` goes true again, and
// data back to null, when the show changes. Without it the null-show fetch
// that runs before the show arrives settles to [] and the real fetch, once
// the show lands, runs with `loading` already false, so the screen cannot
// tell "classes still loading" from "this show has no classes". Opt-in
// because AnnouncerContext's default-ring resolution (#1968/#3042) is tuned
// against the unkeyed behaviour and must not change.
//
// #2704 -- two instances run at cold start (AnnouncerContext's ring-resolution
// fetch and RingDataContext's polling one) against the slowest route in the
// app. Concurrent calls for the same show share ONE GET /class (never a cache:
// the next poll always hits the network), and every successful fetch refreshes
// the class-identity cache in utils/classCache.js (a whitelist projection, no
// ride-derived field) that lets the next cold start resolve the ring before
// this answers.
// `shareWith` (optional) is the coalescing owner: instances given the same owner
// object share an in-flight request (AnnouncerProvider hands its own to both).
export function useClassesForShow(showId, nonce = 0, { keyed = false, shareWith = null } = {}) {
  const api = useScoreApi();
  return useRemoteResource(
    async () => {
      if (!showId) return [];
      const load = async () => unwrap(await api.classes.listClasses(showId));
      const rows = shareWith ? await coalesceInflight(shareWith, `class:${showId}`, load) : await load();
      writeClassCache(showId, rows);
      return rows;
    },
    [api, showId, nonce, shareWith],
    keyed ? [showId ?? null] : undefined,
  );
}

export default useClassesForShow;
